// VIDEO-EDITING — pure helpers used by the store + the save hook.
import { SOURCE_LANGUAGE_ID, type TranscriptSegment, type VideoProject } from "../types";
import type { ProjectDraft, UiState, SseState } from "./types";

export const EMPTY_DRAFT: ProjectDraft = {
  draftPresetId: null,
  draftSegments: null,
  draftCustomStyle: null,
  draftOverlays: null,
  draftOverlayTranslations: null,
  draftClips: null,
  draftBrolls: null,
  timelineOps: [],
  draftLanguageSegments: {},
  lastEditedAt: 0,
};

export const EMPTY_UI: UiState = {
  activeRightPane: "transcript",
  activeLanguage: SOURCE_LANGUAGE_ID,
  captionEditMode: false,
  selectedOverlayId: null,
  videoAspect: null,
  videoDurationSec: null,
  brollBusy: false,
  brollError: null,
  exportMenuOpen: false,
  editingIdx: null,
  editingValue: "",
};

export const EMPTY_SSE: SseState = {
  translating: null,
  translateError: null,
  translateProgress: null,
  exporting: null,
  exportError: null,
  exportProgress: null,
  transcribing: false,
  transcribeError: null,
  transcribeProgress: null,
};

export const EMPTY_SEGMENTS: TranscriptSegment[] = [];

// Deep-ish equality for transcript segments. Cheap JSON.stringify is
// good enough — segments are short and the comparison happens on action,
// not every render.
function segmentsEqual(
  a: TranscriptSegment[] | null | undefined,
  b: TranscriptSegment[] | null | undefined,
): boolean {
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

// Whether any draft bucket has user edits not yet persisted to the
// server. Cheap enough to call from a selector — JSON-stringify is the
// hot path inside but the inputs are short.
export function computeDirty(
  draft: ProjectDraft | null | undefined,
  project: VideoProject | null,
  activeLanguage?: string,
): boolean {
  if (!draft || !project) return false;
  if (
    draft.draftPresetId != null &&
    draft.draftPresetId !== (project.presetId ?? "trust-bar")
  ) {
    return true;
  }
  if (draft.draftSegments != null) {
    const lang = activeLanguage || SOURCE_LANGUAGE_ID;
    const baseSegments =
      lang === SOURCE_LANGUAGE_ID
        ? project.transcript?.segments ?? null
        : project.translations?.find((t) => t.language === lang)?.segments ?? null;
    if (!segmentsEqual(draft.draftSegments, baseSegments)) {
      return true;
    }
  }
  if (
    draft.draftCustomStyle != null &&
    JSON.stringify(draft.draftCustomStyle) !==
      JSON.stringify(project.customStyle ?? null)
  ) {
    return true;
  }
  if (
    draft.draftOverlays != null &&
    JSON.stringify(draft.draftOverlays) !==
      JSON.stringify(project.overlays ?? [])
  ) {
    return true;
  }
  if (
    draft.draftOverlayTranslations != null &&
    JSON.stringify(draft.draftOverlayTranslations) !==
      JSON.stringify(project.overlayTranslations ?? [])
  ) {
    return true;
  }
  if (
    draft.draftClips != null &&
    JSON.stringify(draft.draftClips) !==
      JSON.stringify(project.clips ?? [])
  ) {
    return true;
  }
  if (
    draft.draftBrolls != null &&
    JSON.stringify(draft.draftBrolls) !==
      JSON.stringify(project.brolls ?? [])
  ) {
    return true;
  }
  // `draftMusic === undefined` means no in-flight edit; null or an object is
  // a real edit — compare against the persisted selection (treating
  // missing/null project music as the same "no music" state).
  if (
    draft.draftMusic !== undefined &&
    JSON.stringify(draft.draftMusic ?? null) !==
      JSON.stringify(project.music ?? null)
  ) {
    return true;
  }
  if (draft.timelineOps.length > 0) return true;
  return false;
}

// Drop draft keys that match the server snapshot — the PATCH body should
// only carry actual deltas. Safe to call with a partially-edited draft.
export function prunePatch(
  draft: ProjectDraft,
  project: VideoProject,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  if (
    draft.draftPresetId != null &&
    draft.draftPresetId !== (project.presetId ?? null)
  ) {
    patch.presetId = draft.draftPresetId;
  }
  if (draft.draftCustomStyle != null) {
    patch.customStyle = draft.draftCustomStyle;
  }
  if (draft.draftOverlays != null) {
    patch.overlays = draft.draftOverlays;
  }
  if (draft.draftOverlayTranslations != null) {
    patch.overlayTranslations = draft.draftOverlayTranslations;
  }
  if (draft.draftClips != null) {
    patch.clips = draft.draftClips;
  }
  if (draft.draftBrolls != null) {
    patch.brolls = draft.draftBrolls;
  }
  if (draft.draftMusic !== undefined) {
    patch.music = draft.draftMusic ?? null;
  }
  return patch;
}

// Maintain the localStorage cap — drop the oldest drafts past
// `maxEntries` ordered by `lastEditedAt` descending.
export function pruneOldestDrafts(
  drafts: Record<string, ProjectDraft>,
  maxEntries: number,
): Record<string, ProjectDraft> {
  const entries = Object.entries(drafts);
  if (entries.length <= maxEntries) return drafts;
  entries.sort((a, b) => b[1].lastEditedAt - a[1].lastEditedAt);
  return Object.fromEntries(entries.slice(0, maxEntries));
}
